import type { UserRole } from './auth.js';

/** Catalog types (F-02, DATA-MODEL.md). No assigned-streaming-platform field on assets. */

export const ASSET_TYPES = [
  'laptop',
  'clock',
  'camera',
  'mouse',
  'extension_cord',
  'mount',
  'tripod',
  'router',
  'switch',
  'ap',
  'nano',
  'printer',
  'psu',
  'mat',
  'other',
] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  laptop: 'Laptop',
  clock: 'Clock',
  camera: 'Camera',
  mouse: 'Mouse',
  extension_cord: 'Extension cord',
  mount: 'Mount',
  tripod: 'Tripod',
  router: 'Router',
  switch: 'Switch',
  ap: 'Access point',
  nano: 'Nano',
  printer: 'Printer',
  psu: 'Power supply',
  mat: 'Mat',
  other: 'Other',
};

export const ASSET_STATUSES = [
  'available',
  'assigned',
  'in_transit',
  'at_event',
  'missing',
  'broken',
  'retired',
] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  available: 'Available',
  assigned: 'Assigned',
  in_transit: 'In transit',
  at_event: 'At event',
  missing: 'Missing',
  broken: 'Broken',
  retired: 'Retired',
};

export const TOTE_TYPES = ['clock_kit', 'head_table', 'network', 'spare', 'other'] as const;
export type ToteType = (typeof TOTE_TYPES)[number];

export const TOTE_TYPE_LABELS: Record<ToteType, string> = {
  clock_kit: 'Clock kit',
  head_table: 'Head table',
  network: 'Network',
  spare: 'Spare',
  other: 'Other',
};

export const TOTE_STATUSES = ['active', 'retired'] as const;
export type ToteStatus = (typeof TOTE_STATUSES)[number];

export const CONTAINER_KINDS = ['tote', 'bin', 'trailer', 'uncontained'] as const;
export type ContainerKind = (typeof CONTAINER_KINDS)[number];

export const CONTAINER_KIND_LABELS: Record<ContainerKind, string> = {
  tote: 'Tote',
  bin: 'Bin',
  trailer: 'Trailer',
  uncontained: 'Uncontained',
};

export const CONSUMABLE_UNITS = ['each', 'roll', 'pack'] as const;
export type ConsumableUnit = (typeof CONSUMABLE_UNITS)[number];

export const CONSUMABLE_UNIT_LABELS: Record<ConsumableUnit, string> = {
  each: 'Each',
  roll: 'Roll',
  pack: 'Pack',
};

/** Printed QR codes always point at production (ADR-009). Never a preview or staging host. */
export const PRODUCTION_ORIGIN = 'https://inventohub.ewpok.com';

const URL_SAFE_SEGMENT = /^[A-Za-z0-9._~-]+$/;

export function isAssetType(value: unknown): value is AssetType {
  return typeof value === 'string' && (ASSET_TYPES as readonly string[]).includes(value);
}

export function isAssetStatus(value: unknown): value is AssetStatus {
  return typeof value === 'string' && (ASSET_STATUSES as readonly string[]).includes(value);
}

export function isToteType(value: unknown): value is ToteType {
  return typeof value === 'string' && (TOTE_TYPES as readonly string[]).includes(value);
}

export function isToteStatus(value: unknown): value is ToteStatus {
  return typeof value === 'string' && (TOTE_STATUSES as readonly string[]).includes(value);
}

export function isContainerKind(value: unknown): value is ContainerKind {
  return typeof value === 'string' && (CONTAINER_KINDS as readonly string[]).includes(value);
}

export function isConsumableUnit(value: unknown): value is ConsumableUnit {
  return typeof value === 'string' && (CONSUMABLE_UNITS as readonly string[]).includes(value);
}

export function isUrlSafePathSegment(value: string): boolean {
  return value.length > 0 && URL_SAFE_SEGMENT.test(value);
}

/** Retired totes stay in the catalog but cannot go on a loadout until un-retired. */
export function isToteAssignable(status: ToteStatus): boolean {
  return status === 'active';
}

/** Shop packing onto a trailer or shipment is totes plus loose mats only. */
export function isPackableAsset(asset: { type: string }): boolean {
  return asset.type === 'mat';
}

export function roleCanEditCatalog(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin';
}

/** Pack totes and mats onto a trailer or shipment (F-13). Same as catalog edit. */
export function roleCanPack(role: UserRole): boolean {
  return roleCanEditCatalog(role);
}

/** Shop moves (F-03). Same standing roles as catalog writes; TD codes never move (F-08). */
export function roleCanMoveAssets(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin';
}

/** Missing and broken stick until an admin or equipment admin clears them (F-07). */
export function isExceptionStatus(status: AssetStatus): boolean {
  return status === 'missing' || status === 'broken';
}

export function roleCanClearException(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin';
}

export function isUsableAssetStatus(status: AssetStatus): boolean {
  return status !== 'missing' && status !== 'broken' && status !== 'retired';
}

export function exceptionAssetsInTote(
  toteId: string,
  assets: readonly PublicAsset[],
): PublicAsset[] {
  return assets.filter(
    (asset) =>
      asset.containerKind === 'tote' &&
      asset.containerId === toteId &&
      isExceptionStatus(asset.status),
  );
}

export function toteQrPath(number: string, qrSlug: string): string {
  return isUrlSafePathSegment(number) ? `/t/${number}` : `/q/${qrSlug}`;
}

export function assetQrPath(serial: string, qrSlug: string): string {
  return isUrlSafePathSegment(serial) ? `/a/${serial}` : `/q/${qrSlug}`;
}

export function qrUrl(path: string): string {
  return `${PRODUCTION_ORIGIN}${path}`;
}

export function toteQrUrl(number: string, qrSlug: string): string {
  return qrUrl(toteQrPath(number, qrSlug));
}

export function assetQrUrl(serial: string, qrSlug: string): string {
  return qrUrl(assetQrPath(serial, qrSlug));
}

export interface AssetTypeRecord {
  slug: AssetType;
  label: string;
  sortOrder: number;
  active: boolean;
}

export interface PublicAsset {
  id: string;
  serial: string;
  type: AssetType;
  status: AssetStatus;
  containerKind: ContainerKind;
  containerId: string | null;
  containerName: string | null;
  toteNumber: string | null;
  label: string | null;
  notes: string | null;
  purchaseDate: string | null;
  replacementCost: number | null;
  warrantyExpiry: string | null;
  firmware: string | null;
  lastServiceDate: string | null;
  createdAt: string;
  updatedAt: string;
  qrSlug: string;
  qrUrl: string;
}

export interface PublicAssetPhoto {
  id: string;
  assetId: string;
  createdAt: string;
}

export interface PublicTote {
  id: string;
  number: string;
  type: ToteType;
  status: ToteStatus;
  notes: string | null;
  qrSlug: string;
  qrUrl: string;
  assignable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicBin {
  id: string;
  name: string;
  locationNote: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicTrailer {
  id: string;
  name: string;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicShipment {
  id: string;
  name: string;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export const SHIPPED_VIA_KINDS = ['trailer', 'shipment'] as const;
export type ShippedViaKind = (typeof SHIPPED_VIA_KINDS)[number];

export function isShippedViaKind(value: unknown): value is ShippedViaKind {
  return typeof value === 'string' && (SHIPPED_VIA_KINDS as readonly string[]).includes(value);
}

export interface ShippedViaOption {
  kind: ShippedViaKind;
  id: string;
  name: string;
  notes: string | null;
  active: boolean;
}

export function shippedViaOptionsFrom(
  trailers: readonly PublicTrailer[],
  shipments: readonly PublicShipment[],
): ShippedViaOption[] {
  const options: ShippedViaOption[] = [
    ...trailers.map((trailer) => ({
      kind: 'trailer' as const,
      id: trailer.id,
      name: trailer.name,
      notes: trailer.notes,
      active: trailer.active,
    })),
    ...shipments.map((shipment) => ({
      kind: 'shipment' as const,
      id: shipment.id,
      name: shipment.name,
      notes: shipment.notes,
      active: shipment.active,
    })),
  ];
  return options.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export interface PublicConsumableType {
  id: string;
  name: string;
  unit: ConsumableUnit;
}

export interface PublicContainerConsumable {
  id: string;
  containerKind: 'tote' | 'bin';
  containerId: string;
  consumableType: PublicConsumableType;
  expectedQty: number;
}

export interface AssetListResponse {
  assets: PublicAsset[];
}

export interface AssetResponse {
  asset: PublicAsset;
  photos: PublicAssetPhoto[];
}

export interface ToteListResponse {
  totes: PublicTote[];
}

export interface ToteResponse {
  tote: PublicTote;
  assets: PublicAsset[];
  consumables: PublicContainerConsumable[];
}

export interface BinListResponse {
  bins: PublicBin[];
}

export interface BinResponse {
  bin: PublicBin;
  assets: PublicAsset[];
  consumables: PublicContainerConsumable[];
}

export interface TrailerListResponse {
  trailers: PublicTrailer[];
}

export interface TrailerResponse {
  trailer: PublicTrailer;
  /** Assets whose container is this trailer (F-03 physical location). */
  assets: PublicAsset[];
  /** Kits packed onto this trailer for a trip (F-13). Not tote membership. */
  packedTotes: PublicTote[];
  packedAssets: PublicAsset[];
}

export interface ShipmentListResponse {
  shipments: PublicShipment[];
}

export interface ShipmentResponse {
  shipment: PublicShipment;
  packedTotes: PublicTote[];
  packedAssets: PublicAsset[];
}

export interface AssetTypeListResponse {
  types: AssetTypeRecord[];
}

export interface ConsumableTypeListResponse {
  types: PublicConsumableType[];
}

export interface ConsumableTypeResponse {
  type: PublicConsumableType;
}

export interface ContainerConsumableResponse {
  consumable: PublicContainerConsumable;
}

export interface PublicAssetMove {
  id: string;
  assetId: string;
  fromKind: ContainerKind;
  fromId: string | null;
  fromName: string | null;
  toKind: ContainerKind;
  toId: string | null;
  toName: string | null;
  movedBy: string;
  movedByName: string | null;
  movedAt: string;
  reason: string | null;
}

export interface AssetMoveResponse {
  move: PublicAssetMove;
  asset: PublicAsset;
}

export interface AssetMoveListResponse {
  moves: PublicAssetMove[];
}
